import * as Yup from 'yup';

import { addressEquals } from '../utils';
import { basicAddress } from './basic-address';
import type { AddressItem, EditTripFormData } from './types';

const addressItemSchema = Yup.object<AddressItem>({
  label: Yup.string().optional(),
  value: basicAddress.required(),
});

/**
 * Validates that time is in HH:MM format or ISO 8601 format.
 */
const isValidTime = (value: unknown): boolean => {
  if (!value) return false;
  const timeStr = String(value);
  // Accept HH:MM format
  if (/^([01]\d|2[0-3]):[0-5]\d$/.test(timeStr)) return true;
  // Accept ISO 8601 date-time strings (API prefill values, e.g. "2024-01-15T08:30:00Z")
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(timeStr);
};

/**
 * Validates that pickup address differs from destination address.
 */
const isPickupDifferentFromDestination = (
  value: AddressItem | undefined,
  destinationAddress: AddressItem | undefined
): boolean => {
  return !value || !destinationAddress?.value || !addressEquals(value.value, destinationAddress.value);
};

/**
 * Validates that destination address differs from pickup address.
 */
const isDestinationDifferentFromPickup = (
  value: AddressItem | undefined,
  pickupAddress: AddressItem | undefined
): boolean => {
  return !value || !pickupAddress?.value || !addressEquals(value.value, pickupAddress.value);
};

/**
 * Creates the edit trip Yup validation schema.
 * Shared between web and native platforms.
 * Fixes R-16: Eliminates duplicate validation schemas between platforms.
 *
 * @param t - Translation function to provide localized error messages
 * @returns Yup object schema for EditTripFormData
 */
export const createEditTripSchema = (
  t: (key: string, options?: object, fallbackValue?: string) => string
): Yup.ObjectSchema<EditTripFormData> => {
  return Yup.object().shape({
    time: Yup.string()
      .test('isValidTime', t('common.validation.invalidTime'), isValidTime)
      .required(),
    timeType: Yup.string().oneOf(['pickup', 'destination']).required(),
    pickupAddress: addressItemSchema
      .optional()
      .test(
        'isDifferentToDestination',
        t('edit_address.validation.pickup.same_as_destination'),
        (value: unknown, { parent }) => isPickupDifferentFromDestination(value as AddressItem | undefined, parent.destinationAddress)
      ),
    destinationAddress: addressItemSchema
      .optional()
      .test(
        'isDifferentToPickup',
        t('edit_address.validation.destination.same_as_pickup'),
        (value: unknown, { parent }) => isDestinationDifferentFromPickup(value as AddressItem | undefined, parent.pickupAddress)
      ),
    otherInformation: Yup.string().optional(),
  }) as Yup.ObjectSchema<EditTripFormData>;
};
